import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiLock, FiShield, FiUser } = FiIcons;

const ADMIN_EMAIL_DOMAIN = 'admin.haciendaelgallo.net';

function normalizeUsername(value) {
  return value.trim().toLowerCase();
}

function getAdminEmail(username) {
  return `${normalizeUsername(username)}@${ADMIN_EMAIL_DOMAIN}`;
}

export default function AdminLogin({ onAuthenticated }) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const authenticate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    const username = normalizeUsername(credentials.username);
    const email = getAdminEmail(username);

    // 1. Intentar iniciar sesión con las credenciales ingresadas
    let result = await supabase.auth.signInWithPassword({
      email,
      password: credentials.password
    });

    // 2. Si falla el inicio de sesión y la cuenta aún no existe, registrarla
    if (result.error && (result.error.message === 'Invalid login credentials' || result.error.status === 400)) {
      const signUpResult = await supabase.auth.signUp({
        email,
        password: credentials.password
      });

      if (!signUpResult.error && signUpResult.data?.session) {
        result = signUpResult;
      } else if (signUpResult.error && signUpResult.error.message !== 'User already registered') {
        result = signUpResult;
      }
    }

    // 3. Manejar errores específicos de Supabase
    if (result.error) {
      if (result.error.status === 429) {
        setError('Demasiados intentos (Error 429). Por favor, espera unos minutos.');
      } else if (result.error.message === 'User already registered') {
        setError('Contraseña incorrecta para el usuario ' + username);
      } else if (result.error.message === 'Email not confirmed') {
        setError('Debes desactivar "Confirm email" en Supabase Auth Settings.');
      } else if (result.error.message === 'Invalid login credentials') {
        setError('Usuario o contraseña incorrectos.');
      } else {
        setError(`Error: ${result.error.message}`);
      }
      setLoading(false);
      return;
    }

    // 4. Si no hay error pero tampoco hay sesión activa
    if (!result.data?.session) {
      setError('Verificación requerida. Asegúrate de tener desactivada la opción "Confirm email" en Supabase.');
      setLoading(false);
      return;
    }

    try {
      await supabase.auth.setSession({
        access_token: result.data.session.access_token,
        refresh_token: result.data.session.refresh_token
      });
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (e) {
      console.warn('Error actualizando sesión:', e);
    }

    // 5. Reclamar permisos de administrador en base de datos
    const { data: authorized, error: claimError } = await supabase.rpc(
      'claim_first_chat_admin'
    );

    if (claimError) {
      await supabase.auth.signOut();
      setError(`Error de base de datos (${claimError.code}): ${claimError.message}`);
      setLoading(false);
      return;
    }

    if (!authorized) {
      await supabase.auth.signOut();
      setError('Esta cuenta no cuenta con permisos administrativos.');
      setLoading(false);
      return;
    }

    // 6. Actualizar perfil del administrador
    await supabase
      .from('chat_admins_1786723472726')
      .update({ username, email })
      .eq('user_id', result.data.session.user.id);

    onAuthenticated(result.data.session);
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-7 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg shadow-red-600/30">
            <SafeIcon icon={FiShield} size={26} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-wide">
            Administración
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Acceso exclusivo para Hacienda El Gallo
          </p>
        </div>

        <form onSubmit={authenticate} className="space-y-4" autoComplete="off">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-gray-400">
              Usuario
            </span>
            <div className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 focus-within:border-red-600 transition-colors">
              <SafeIcon icon={FiUser} className="text-gray-500" />
              <input
                type="text"
                required
                minLength={3}
                maxLength={30}
                placeholder="Ingresa tu usuario"
                autoComplete="username"
                value={credentials.username}
                onChange={(event) =>
                  setCredentials({ ...credentials, username: event.target.value })
                }
                className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-gray-600 outline-none"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-gray-400">
              Contraseña
            </span>
            <div className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 focus-within:border-red-600 transition-colors">
              <SafeIcon icon={FiLock} className="text-gray-500" />
              <input
                type="password"
                required
                minLength={6}
                placeholder="Ingresa tu contraseña"
                autoComplete="current-password"
                value={credentials.password}
                onChange={(event) =>
                  setCredentials({ ...credentials, password: event.target.value })
                }
                className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-gray-600 outline-none"
              />
            </div>
          </label>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400">
              <p className="font-semibold mb-1">No se pudo iniciar sesión:</p>
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-red-600 py-3.5 text-sm font-bold text-white transition-all hover:bg-red-700 active:scale-98 disabled:opacity-50 shadow-lg shadow-red-600/20"
          >
            {loading ? 'Verificando acceso...' : 'Ingresar al Panel'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a
            href="#/"
            className="inline-block text-xs text-gray-500 hover:text-white transition-colors"
          >
            Volver a la transmisión
          </a>
        </div>
      </div>
    </div>
  );
}